import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../context/AuthContext'
import { getStorageUrl } from '../../utils/constants'
import { notificationsApi } from '../../api/notifications'
import { FiSearch, FiMenu, FiX, FiUser, FiBell, FiLogOut, FiHome, FiPackage, FiShoppingBag, FiTrendingUp } from 'react-icons/fi'
import './Header.css'

const Header = () => {
  const { user, isAuthenticated, logout } = useAuth()
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const userMenuRef = useRef(null)
  const navbarRef = useRef(null)

  // Fetch notifications to get unread count
  const { data: notificationsData } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.getNotifications(),
    enabled: isAuthenticated,
    refetchInterval: 30000, // Refetch every 30 seconds
  })

  // Calculate unread notifications count
  const notifications = notificationsData?.data?.notifications || []
  const unreadCount = notifications.filter(notification => !notification.read).length

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true)
        if (navbarRef.current) {
          navbarRef.current.classList.add('bg-gray-900/98', 'backdrop-blur-md', 'shadow-xl', 'py-3')
          navbarRef.current.classList.remove('bg-gray-900/95', 'py-4')
        }
      } else {
        setIsScrolled(false)
        if (navbarRef.current) {
          navbarRef.current.classList.remove('bg-gray-900/98', 'backdrop-blur-md', 'shadow-xl', 'py-3')
          navbarRef.current.classList.add('bg-gray-900/95', 'py-4')
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll() // Initial check
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleLogout = async () => {
    await logout()
    navigate('/')
    setUserMenuOpen(false)
  }

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false)
      }
    }

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [userMenuOpen])

  return (
    <nav id="navbar" ref={navbarRef} className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-gray-900/95 backdrop-blur-md py-4 border-b border-gray-800">
      <div className="container mx-auto px-4 md:px-8 flex items-center justify-between">
        {/* Logo Area */}
        <div className="flex items-center gap-3">
          <div className="w-16 h-16 relative flex items-center justify-center">
            <img 
              src="/logo.png" 
              alt="3afer" 
              className="w-14 h-14 object-contain"
              onError={(e) => {
                // Hide logo if it fails to load (404)
                e.target.style.display = 'none'
              }}
            />
          </div>
          <div className="flex flex-col">
            <span className="text-2xl font-extrabold text-white leading-none">عافر</span>
          </div>
        </div>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center gap-8">
          <Link to="/" className="font-medium text-gray-300 hover:text-white transition-colors">الرئيسية</Link>
          <Link to="/my-courses" className="font-medium text-gray-300 hover:text-white transition-colors">الدورات</Link>
          <Link to="/subscriptions" className="font-medium text-gray-300 hover:text-white transition-colors flex items-center gap-2">
            <FiPackage className="w-4 h-4" />
            الباقات
          </Link>
          <Link to="/instructors" className="font-medium text-gray-300 hover:text-white transition-colors">المحاضرين</Link>
          <Link to="/community" className="font-medium text-gray-300 hover:text-white transition-colors">المجتمع</Link>
        </div>

        {/* Actions */}
        <div className="hidden md:flex items-center gap-4">
          <button 
            className="text-gray-400 hover:text-white transition-colors" 
            onClick={() => navigate('/search')}
            aria-label="بحث"
            title="بحث"
          >
            <FiSearch className="w-6 h-6" />
          </button>
          <span className="h-6 w-px bg-gray-700"></span>
          {isAuthenticated ? (
            <>
              <Link to="/notifications" className="text-gray-400 hover:text-white relative">
                <FiBell className="w-6 h-6" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-accent-orange rounded-full text-white text-[10px] font-bold flex items-center justify-center px-1">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Link>
              <div className="user-menu-wrapper relative" ref={userMenuRef}>
                <button
                  className="w-10 h-10 rounded-full overflow-hidden border-2 border-gray-700 hover:border-white transition-colors"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  {user?.avatar ? (
                    <img src={getStorageUrl(user.avatar)} alt={user.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-primary-blue flex items-center justify-center text-white">
                      <FiUser className="w-5 h-5" />
                    </div>
                  )}
                </button>
                {userMenuOpen && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-gray-800 rounded-xl shadow-xl border border-gray-700 p-2 z-50">
                    <Link to="/profile" className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 rounded-lg" onClick={() => setUserMenuOpen(false)}>
                      <FiUser className="inline w-4 h-4 mr-2" /> الملف الشخصي
                    </Link>
                    <Link to="/profile/progress" className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 rounded-lg" onClick={() => setUserMenuOpen(false)}>
                      <FiTrendingUp className="inline w-4 h-4 mr-2" /> مستوايا
                    </Link>
                    <Link to="/my-courses" className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 rounded-lg" onClick={() => setUserMenuOpen(false)}>
                      دوراتي
                    </Link>
                    <Link to="/subscriptions" className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 rounded-lg flex items-center gap-2" onClick={() => setUserMenuOpen(false)}>
                      <FiPackage className="w-4 h-4" />
                      الباقات
                    </Link>
                    <Link to="/favorites" className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 rounded-lg" onClick={() => setUserMenuOpen(false)}>
                      المفضلة
                    </Link>
                    <Link to="/settings" className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 rounded-lg" onClick={() => setUserMenuOpen(false)}>
                      الإعدادات
                    </Link>
                    <div className="h-px bg-gray-700 my-2"></div>
                    <button className="w-full text-right px-4 py-2 text-sm text-red-400 hover:bg-red-900/20 rounded-lg" onClick={handleLogout}>
                      <FiLogOut className="inline w-4 h-4 mr-2" /> تسجيل الخروج
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/login" className="font-bold text-white hover:text-gray-300 transition-colors">تسجيل الدخول</Link>
              <Link to="/register" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold transition-all duration-300 transform hover:-translate-y-1 active:scale-95 text-sm md:text-base bg-primary-blue text-white hover:bg-[#0052a3] shadow-lg hover:shadow-blue-500/30">
                حساب جديد
              </Link>
            </>
          )}
        </div>

        {/* Mobile Toggle */}
        <button
          id="mobile-menu-toggle"
          className="md:hidden text-gray-300"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" id="x-icon">
              <path d="M18 6 6 18"/>
              <path d="m6 6 12 12"/>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" id="menu-icon">
              <line x1="4" x2="20" y1="12" y2="12"/>
              <line x1="4" x2="20" y1="6" y2="6"/>
              <line x1="4" x2="20" y1="18" y2="18"/>
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Menu Dropdown */}
      <div
        id="mobile-menu"
        className={`md:hidden absolute top-full left-0 right-0 bg-gray-900 border-t border-gray-800 shadow-xl p-4 flex-col gap-4 ${mobileMenuOpen ? 'flex' : 'hidden'}`}
      >
        <Link to="/" className="p-2 font-medium text-gray-300 hover:bg-gray-800 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
          الرئيسية
        </Link>
        <Link to="/my-courses" className="p-2 font-medium text-gray-300 hover:bg-gray-800 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
          الدورات
        </Link>
        <Link to="/subscriptions" className="p-2 font-medium text-gray-300 hover:bg-gray-800 rounded-lg flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
          <FiPackage className="w-4 h-4" />
          الباقات
        </Link>
        <Link to="/instructors" className="p-2 font-medium text-gray-300 hover:bg-gray-800 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
          المحاضرين
        </Link>
        <div className="h-px bg-gray-800 my-2"></div>
        {isAuthenticated ? (
          <>
            <Link to="/profile" className="p-2 font-medium text-gray-300 hover:bg-gray-800 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
              الملف الشخصي
            </Link>
            <Link to="/my-courses" className="p-2 font-medium text-gray-300 hover:bg-gray-800 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
              دوراتي
            </Link>
            <button className="p-2 font-medium text-red-400 hover:bg-red-900/20 rounded-lg text-right w-full" onClick={handleLogout}>
              تسجيل الخروج
            </button>
          </>
        ) : (
          <>
            <Link to="/login" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold transition-all duration-300 active:scale-95 text-sm md:text-base w-full border-2 border-primary-blue text-primary-blue hover:bg-blue-900/20" onClick={() => setMobileMenuOpen(false)}>
              تسجيل الدخول
            </Link>
            <Link to="/register" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold transition-all duration-300 transform hover:-translate-y-1 active:scale-95 text-sm md:text-base w-full bg-primary-blue text-white hover:bg-[#0052a3] shadow-lg hover:shadow-blue-500/30" onClick={() => setMobileMenuOpen(false)}>
              حساب جديد
            </Link>
          </>
        )}
      </div>
    </nav>
  )
}

export default Header

